import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'

import { CheckMark, CrossIcon } from './fields'

const ToastContext = createContext<(text: string, tone?: 'ok' | 'error') => void>(() => {})

type Toast = { text: string; tone: 'ok' | 'error' }

export function useToast(): (text: string, tone?: 'ok' | 'error') => void {
  return useContext(ToastContext)
}

function toastDuration(): number {
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--duration-toast')
  return Number.parseFloat(raw) || 1400
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toast, setToast] = useState<Toast>()
  const timer = useRef<number>(0)

  const show = useCallback((text: string, tone: 'ok' | 'error' = 'ok') => {
    setToast({ text, tone })
    window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => setToast(undefined), toastDuration())
  }, [])

  const value = useMemo(() => show, [show])

  return (
    <ToastContext.Provider value={value}>
      {children}
      {toast ? (
        <div className="pointer-events-none fixed inset-x-0 top-[calc(10px+(var(--spacing-safe-top)))] z-50 flex justify-center px-4">
          <div className="animate-toast-in flex h-[34px] items-center gap-2 rounded-capsule bg-toast px-[13px] text-meta font-medium leading-none text-toast-fg shadow-float">
            {toast.tone === 'ok' ? <CheckMark size={13} /> : <CrossIcon size={11} />}
            {toast.text}
          </div>
        </div>
      ) : null}
    </ToastContext.Provider>
  )
}
